import React, { useState } from 'react';
import { X, Search, Package, Scissors, Sparkles, Truck, CheckCircle } from 'lucide-react';

// Demo order data for showcase
const demoOrders = {
  'SILK2024001': {
    id: 'SILK2024001',
    clientName: 'Meera S.',
    piece: 'Noor-e-Chandni Gharara',
    fabric: 'Pure Katan Silk',
    embroidery: 'Chikankari & Mukaish',
    status: 'embroidery',
    progress: 65,
    estimatedCompletion: '15th January 2025',
    timeline: [
      { stage: 'Order Confirmed', date: '1st December 2024', completed: true, icon: 'package' },
      { stage: 'Fabric Sourced', date: '8th December 2024', completed: true, icon: 'package' },
      { stage: 'Pattern & Cutting', date: '15th December 2024', completed: true, icon: 'scissors' },
      { stage: 'Embroidery in Progress', date: 'Current', completed: false, icon: 'sparkles', current: true },
      { stage: 'Final Finishing', date: 'Upcoming', completed: false, icon: 'sparkles' },
      { stage: 'Quality Check', date: 'Upcoming', completed: false, icon: 'check' },
      { stage: 'Ready for Delivery', date: 'Est. 15th Jan', completed: false, icon: 'truck' }
    ]
  },
  'SILK2024002': {
    id: 'SILK2024002',
    clientName: 'Ananya R.',
    piece: 'Gulbadan Lehenga',
    fabric: 'Heritage Silk Brocade',
    embroidery: 'Zardozi & Gota Patti',
    status: 'finishing',
    progress: 85,
    estimatedCompletion: '28th December 2024',
    timeline: [
      { stage: 'Order Confirmed', date: '15th November 2024', completed: true, icon: 'package' },
      { stage: 'Fabric Sourced', date: '20th November 2024', completed: true, icon: 'package' },
      { stage: 'Pattern & Cutting', date: '28th November 2024', completed: true, icon: 'scissors' },
      { stage: 'Embroidery Complete', date: '18th December 2024', completed: true, icon: 'sparkles' },
      { stage: 'Final Finishing', date: 'Current', completed: false, icon: 'sparkles', current: true },
      { stage: 'Quality Check', date: 'Upcoming', completed: false, icon: 'check' },
      { stage: 'Ready for Delivery', date: 'Est. 28th Dec', completed: false, icon: 'truck' }
    ]
  }
};

const OrderTracking = ({ isOpen, onClose }) => {
  const [orderId, setOrderId] = useState('');
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [showDemo, setShowDemo] = useState(false);

  const getIcon = (iconName) => {
    const iconMap = {
      package: Package,
      scissors: Scissors,
      sparkles: Sparkles,
      truck: Truck,
      check: CheckCircle
    };
    return iconMap[iconName] || Package;
  };

  const handleSearch = () => {
    const trimmedId = orderId.trim().toUpperCase();
    if (demoOrders[trimmedId]) {
      setOrder(demoOrders[trimmedId]);
      setError('');
    } else {
      setOrder(null);
      setError('Order not found. Please check your order ID or contact us on WhatsApp.');
    }
  };

  const handleDemoOrder = (demoId) => {
    setOrder(demoOrders[demoId]);
    setOrderId(demoId);
    setError('');
    setShowDemo(false);
  };

  const resetAndClose = () => {
    setOrderId('');
    setOrder(null);
    setError('');
    setShowDemo(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="order-modal-overlay" onClick={resetAndClose} data-testid="order-tracking-modal">
      <div className="order-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="order-modal-close" onClick={resetAndClose} data-testid="order-close-btn">
          <X size={24} />
        </button>

        <div className="order-modal-header">
          <h2 className="heading-2">The Adda</h2>
          <p className="body-regular order-modal-subtitle">
            Track the journey of your bespoke creation
          </p>
        </div>

        {!order ? (
          <div className="order-search-container">
            <div className="order-search-box">
              <input
                type="text"
                placeholder="Enter your Order ID"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="order-search-input"
                data-testid="order-id-input"
              />
              <button className="btn-primary order-search-btn" onClick={handleSearch} data-testid="order-search-btn">
                <Search size={18} />
                Track Order
              </button>
            </div>
            
            {error && <p className="order-error">{error}</p>}

            <div className="order-demo-section">
              <p className="body-small order-demo-text">
                Don't have an order yet? View our demo to see how we keep you updated.
              </p>
              <button 
                className="btn-secondary order-demo-btn" 
                onClick={() => setShowDemo(!showDemo)}
                data-testid="view-demo-btn"
              >
                {showDemo ? 'Hide Demo' : 'View Demo Orders'}
              </button>
              
              {showDemo && (
                <div className="order-demo-list">
                  {Object.values(demoOrders).map((demo) => (
                    <div 
                      key={demo.id} 
                      className="order-demo-item"
                      onClick={() => handleDemoOrder(demo.id)}
                      data-testid={`demo-order-${demo.id}`}
                    >
                      <span className="order-demo-id">{demo.id}</span>
                      <span className="order-demo-piece">{demo.piece}</span>
                      <span className="order-demo-progress">{demo.progress}% complete</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="order-details">
            <div className="order-info-card">
              <div className="order-info-header">
                <div>
                  <h3 className="heading-3">{order.piece}</h3>
                  <p className="body-small order-info-id">Order ID: {order.id}</p>
                </div>
                <div className="order-progress-circle">
                  <span className="order-progress-value">{order.progress}%</span>
                </div>
              </div>
              <div className="order-info-details">
                <div className="order-info-row">
                  <span className="order-info-label">Fabric:</span>
                  <span className="order-info-value">{order.fabric}</span>
                </div>
                <div className="order-info-row">
                  <span className="order-info-label">Embroidery:</span>
                  <span className="order-info-value">{order.embroidery}</span>
                </div>
                <div className="order-info-row">
                  <span className="order-info-label">Est. Completion:</span>
                  <span className="order-info-value">{order.estimatedCompletion}</span>
                </div>
              </div>
            </div>

            <div className="order-timeline">
              <h4 className="heading-4 order-timeline-title">Creation Journey</h4>
              {order.timeline.map((item, index) => {
                const IconComponent = getIcon(item.icon);
                return (
                  <div 
                    key={index} 
                    className={`order-timeline-item ${item.completed ? 'completed' : ''} ${item.current ? 'current' : ''}`}
                  >
                    <div className="order-timeline-icon">
                      <IconComponent size={18} />
                    </div>
                    <div className="order-timeline-content">
                      <span className="order-timeline-stage">{item.stage}</span>
                      <span className="order-timeline-date">{item.date}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <button 
              className="btn-secondary order-back-btn" 
              onClick={() => setOrder(null)}
              data-testid="order-back-btn"
            >
              Track Another Order
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
